import type { Board } from "../board";
import { DamageType } from "../damageType";
import { Direction, cycleDirection, directionDelta } from "../direction";
import { Timing } from "../effects/effect";
import { DirectionalSlash3Tile } from "../effects/directionalSlash3Tile";
import { MeleeHitMovementEffect } from "../effects/meleeHitMovementEffect";
import { MovementEffect, STANDARD_MOVEMENT_TIME } from "../effects/movementEffect";
import { ParticleEffect } from "../effects/particleEffect";
import { WeaponRechargePopupEffect } from "../effects/weaponRechargePopupEffect";
import type { Rechargeable } from "../interfaces";
import { Mob } from "../mob";
import { ParticleType } from "../particleType";
import { STD_BASE_HP } from "../physicalThingWithHp";
import type { Player } from "../player";
import { isPushImmune, pushMob } from "../pushMob";
import { Plant } from "../things/plant";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { ToxicSlugBrokenShell } from "../things/toxicSlugBrokenShell";
import { Weapon } from "../weapon";
import { WeaponVisual } from "../weaponVisual";
import type { XY } from "../xy";

export class Cleaver extends Weapon implements Rechargeable {
	static key = "Cleaver";
	static readonly RECHARGE_TURNS = 0;
	static readonly PUSH_DISTANCE = 2;
	static readonly DAMAGE = STD_BASE_HP * 0.4;
	private cooldownRemaining = 0;

	constructor(owner: Player) {
		super(owner, 0, true);
	}

	override getAutoFireRange(): number {
		return 2;
	}

	override getWeaponVisual(): WeaponVisual {
		return WeaponVisual.CLEAVER;
	}

	getCooldownTurns(): number {
		return this.cooldownRemaining;
	}

	tickCooldown(): void {
		if (this.cooldownRemaining > 0) this.cooldownRemaining--;
	}

	override pullTrigger(direction: Direction, board: Board): boolean {
		if (this.cooldownRemaining > 0) {
			board.addNonPhysicalThing(new Sound(SoundId.TERMINAL_ERROR));
			board.addEffect(new WeaponRechargePopupEffect(this.owner.xy));
			board.turnAbortedByPlayer = true;
			return false;
		}

		// Make the strike resolve before mob turns in animation too, not just in state.
		const effectsStart = board.lastTurnEffects.length;
		const thingsStart = board.getAllThings().length;
		this.fire(direction, board);
		this.resolveBeforeMobs(board, effectsStart, thingsStart);

		this.cooldownRemaining = Cleaver.RECHARGE_TURNS;
		board.stats.recordShotFired(Cleaver.key);
		return true;
	}

	fire(direction: Direction, board: Board): void {
		const origin = this.owner.xy;
		const frontXY = origin.add(directionDelta(direction));
		board.addNonPhysicalThing(new Sound(SoundId.CLEAVER_ATTACK, origin));

		// Strike before attempting the dash: killing (or pushing away) the mob in front
		// frees the cell, letting the player dash through their own kill.
		const alreadyHit = new Set<Mob>();
		this.hitFan(board, origin, direction, alreadyHit);

		if (this.canDash(board, frontXY)) {
			this.owner.xy = frontXY;
			const move = new MovementEffect(this.owner, origin, frontXY);
			board.addEffect(move);
			board.addEffect(new ParticleEffect(origin, ParticleType.STEP_DUST, 1));
			board.stats.recordTileWalked();
			const slash = new DirectionalSlash3Tile(origin, direction);
			slash.setTravel(frontXY, STANDARD_MOVEMENT_TIME);
			slash.setAnimationOrder(move.getAnimationOrder());
			board.addEffect(slash);
			this.hitFan(board, frontXY, direction, alreadyHit);
		} else {
			board.addEffect(new MeleeHitMovementEffect(this.owner, origin, frontXY));
			board.addEffect(new DirectionalSlash3Tile(origin, direction));
		}
	}

	private canDash(board: Board, target: XY): boolean {
		return board.isInBounds(target) && !board.hasMob(target) && board.isPassable(target);
	}

	private hitFan(board: Board, center: XY, direction: Direction, alreadyHit: Set<Mob>): void {
		const fanDirections = [cycleDirection(direction, -1), direction, cycleDirection(direction, 1)];
		for (const dir of fanDirections) {
			const targetXY = center.add(directionDelta(dir));
			if (!board.isInBounds(targetXY)) continue;
			for (const thing of board.getThingsAt(targetXY)) {
				if (!thing.active) continue;
				if (thing instanceof Plant || thing instanceof ToxicSlugBrokenShell) {
					thing.kill(DamageType.MELEE, this.owner);
					continue;
				}
				if (!(thing instanceof Mob) || alreadyHit.has(thing)) continue;
				alreadyHit.add(thing);
				const damageDealt = thing.applyDamage(DamageType.MELEE, Cleaver.DAMAGE, this.owner);
				if (damageDealt > 0 && thing.active) {
					thing.staggerAttack();
					if (isPushImmune(thing)) {
						thing.staggerMovement();
					} else {
						const r = pushMob(board, thing, direction, Cleaver.PUSH_DISTANCE, this.owner);
						if (r.landedOnFlyable) thing.applyDamage(DamageType.EXPLOSION, thing.hp, this.owner);
						if (!r.moved) thing.staggerMovement();
					}
				}
			}
		}
	}

	// New effects/sounds since the snapshot are all this strike's (player acts first); shift them
	// below order 0 so they animate before mobs' order-0 moves. Mirrors Sword.shiftOrdersBelowZero.
	private resolveBeforeMobs(board: Board, effectsStart: number, thingsStart: number): void {
		const myEffects = board.lastTurnEffects.slice(effectsStart);
		const allThings = board.getAllThings();
		const mySounds: Sound[] = [];
		for (let i = thingsStart; i < allThings.length; i++) {
			if (allThings[i] instanceof Sound) mySounds.push(allThings[i] as Sound);
		}

		let maxOrder = -Infinity;
		for (const e of myEffects) maxOrder = Math.max(maxOrder, e.getAnimationOrder());
		for (const s of mySounds) maxOrder = Math.max(maxOrder, s.getAnimationOrder());
		if (maxOrder === -Infinity) return;

		const shift = -(maxOrder + 1);
		if (shift >= 0) return;
		for (const e of myEffects) e.setAnimationOrder(e.getAnimationOrder() + shift);
		for (const s of mySounds) {
			s.setTiming(Timing.BEFORE_STATE_UPDATE);
			s.setAnimationOrder(s.getAnimationOrder() + shift);
		}
	}

	override serialize(): Record<string, unknown> {
		return { ...super.serialize(), cooldownRemaining: this.cooldownRemaining };
	}

	static deserialize(data: any, owner: Player): Cleaver {
		const w = new Cleaver(owner);
		w.cooldownRemaining = data.cooldownRemaining ?? 0;
		return w;
	}
}
